import { Injectable, OnDestroy } from "@angular/core";
import { catchError, EMPTY, finalize, ReplaySubject, Subject, takeUntil, tap } from "rxjs";
import { LaborOperation } from "../models/labor-operation";
import { PartComponent } from "../models/part-component";
import { ReferenceService } from "./reference.service";
import { AuthService } from "../../auth/auth.service";

export interface IReferenceCache {
    /**
     * Subject for part components.
     */
    componentsSubject: ReplaySubject<PartComponent[]>;
    /**
     * Subject for labor operations.
     */
    laborOperationsSubject: ReplaySubject<LaborOperation[]>;
    /**
     * Indicates if the component options are being updated.
     */
    updatingComponents: boolean;
    /**
     * Indicates if the labor operation options are being updated.
     */
    updatingLaborOperations: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ReferenceCache implements IReferenceCache, OnDestroy {
    public readonly dataError$ = new Subject<void>();
    /**
     * A subject that can be used to automatically end subscriptions when the component is destroyed.
     * @private
     */
    private readonly _destroying$ = new Subject<void>();
    
    private components: PartComponent[] = [];
    private componentGroups: string[] = [];
    private laborOperations: LaborOperation[] = [];

    /**
     * Subject for part components.
     */
    public componentsSubject = new ReplaySubject<PartComponent[]>(1);
    /**
     * Subject for component groups.
     */
    public componentGroupsSubject = new ReplaySubject<string[]>(1);
    /**
     * Subject for labor operations.
     */
    public laborOperationsSubject = new ReplaySubject<LaborOperation[]>(1);

    /**
     * Indicates if the component options are being updated.
     */
    public updatingComponents: boolean = false;
    /**
     * Indicates if the labor operation options are being updated.
     */
    public updatingLaborOperations: boolean = false;

    constructor(private referenceService: ReferenceService,
                private authService: AuthService) {
        this.init();
    }

    ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
    }

    /**
     * Initializes the reference cache.
     */
    init(): void {
        this.authService.loginStatusChanged
            .pipe(takeUntil(this._destroying$))
            // Refresh all cached data when the login status changes.
            .subscribe(() => this.refreshAll());
        // If the component starts with the user logged in, perform a refresh.
        if (this.authService.isLoggedIn()) this.refreshAll();
    }

    /**
     * Refreshes all cache data.
     */
    private refreshAll(): void {
        // If not logged in, don't perform a refresh.
        if (!this.authService.isLoggedIn()) return;
        //TODO: If not logged in, wait until a login is complete.

        this.refreshPartComponents();
        this.refreshLaborComponents();
    }

    /**
     * Refreshes part components in the cache.
     */
    private refreshPartComponents(): void {
        this.updatingComponents = true;
        this.referenceService.getPartComponents()
                .pipe(takeUntil(this._destroying$),
                    catchError(err => {
                        this.dataError$.next();
                        this.componentsSubject.error(err);
                        return EMPTY;
                    }),
                    tap(results => {
                        if(!results || results.length === 0) {
                            this.dataError$.next();
                        }}),
                    finalize(() => this.updatingComponents = false)
                )
                .subscribe(results => {
                        this.components = results;
                        this.componentsSubject.next(this.components);
                        this.componentGroups = [...new Set(this.components.map(c => c.groupName))];
                        this.componentGroupsSubject.next(this.componentGroups);
                    }
                );
    }
    /**
     * Refreshes labor components in the cache.
     */
    private refreshLaborComponents(): void {        
        this.updatingLaborOperations = true;
        this.referenceService.getLaborOperations()
            .pipe(takeUntil(this._destroying$),
                catchError(err => {
                    this.dataError$.next();
                    this.laborOperationsSubject.error(err);
                    return EMPTY;
                }),
                tap(results => {
                    if(!results || results.length === 0) {
                        this.dataError$.next();
                    }
                }),
                finalize(() => this.updatingLaborOperations = false)
            )
            .subscribe(results => {
                    this.laborOperations = results;
                    this.laborOperationsSubject.next(this.laborOperations.filter(o => o.isActive)
                                                        .sort((a, b) => a.sortOrder - b.sortOrder));
                }
            );
    }
}